import { MealType } from 'common/MealTypeUtils';
import { useCallback, useEffect, useMemo } from 'react';
import { DishFirstFoodItemPreviewFragment } from 'src/gqlReactTypings.generated.d';
import { useWindowScroll } from 'src/pages/consumer/components/DishFirstHeader/hooks/useWindowScroll';
import { sortItemsByMealType } from '../components/Segments/utils';
import { MEAL_TYPE_SECTION_OFFSET_TOP, MEAL_TYPE_SELECTOR } from '../Constants';
import { useMealTypeMenuBarContext } from '../context';

export type UseGetCurrentMealTypeProps = {
  foodItems: DishFirstFoodItemPreviewFragment[];
};

export const useSetActiveMealTypeOnScroll = () => {
  const { setActiveMealType } = useMealTypeMenuBarContext();

  const handleScroll = useCallback(() => {
    const mealTypeSegments: NodeListOf<HTMLDivElement> = window.document.querySelectorAll(
      `[data-role="${MEAL_TYPE_SELECTOR}"]`
    );
    const { currentSection } = Array.from(mealTypeSegments).reduce(
      (acc, cur) => {
        const { top } = cur.getBoundingClientRect();
        const currentOffset = top + window.pageYOffset - MEAL_TYPE_SECTION_OFFSET_TOP;
        if (window.pageYOffset > currentOffset && currentOffset > acc.offset) {
          return {
            currentSection: cur.getAttribute('data-meal-type') ?? '',
            offset: currentOffset,
          };
        }
        return acc;
      },
      { currentSection: '', offset: 0 }
    );
    setActiveMealType(currentSection as MealType);
  }, [setActiveMealType]);

  useWindowScroll(handleScroll);
};

export const useSetAvailableMealTypes = () => {
  const { availableMealTypes, setAvailableMealTypes } = useMealTypeMenuBarContext();
  useSetActiveMealTypeOnScroll();

  return { availableMealTypes, setAvailableMealTypes };
};

export const useGetCurrentMealType = (props: UseGetCurrentMealTypeProps) => {
  const { foodItems } = props;
  const { availableMealTypes, setAvailableMealTypes } = useSetAvailableMealTypes();
  const sortedItems = useMemo(() => sortItemsByMealType(foodItems), [foodItems]);
  const hasMultipleMealTypes = Object.values(sortedItems).filter((dishes) => dishes.length > 0).length > 1;

  useEffect(() => {
    const currentMealTypes = Object.keys(sortedItems).filter(
      (mealType) => sortedItems[mealType].length > 0
    ) as MealType[];
    if (availableMealTypes.every((mealType) => currentMealTypes.includes(mealType))) {
      setAvailableMealTypes(currentMealTypes);
    }
  }, [availableMealTypes, setAvailableMealTypes, sortedItems]);

  return { hasMultipleMealTypes, sortedItems };
};
